import { useId } from 'react'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { useTimeCategoriesQuery } from '@/hooks/queries/useTimeCategoriesQuery'
import { cn } from '@/lib/utils'

import type { TimeCategory, TimeEntryCategorySummary } from '@scrambl/shared'

const NO_CATEGORY = 'none'
const FALLBACK_DOT_COLOR = 'hsl(var(--muted-foreground))'

/** The category's color as a small round swatch. */
export function TimeCategoryDot({
  color,
  className,
}: {
  color?: string | null
  className?: string
}) {
  return (
    <span
      aria-hidden="true"
      className={cn('inline-block size-2 shrink-0 rounded-full', className)}
      style={{ backgroundColor: color || FALLBACK_DOT_COLOR }}
    />
  )
}

/** Read-only "● Admin" label for lists and tables. */
export function TimeCategoryLabel({
  category,
  className,
}: {
  category: Pick<TimeEntryCategorySummary, 'name' | 'color'> | null | undefined
  className?: string
}) {
  if (!category) return null
  return (
    <span
      className={cn(
        'inline-flex min-w-0 items-center gap-1.5 text-xs text-muted-foreground',
        className,
      )}
      title={category.name}
    >
      <TimeCategoryDot color={category.color} />
      <span className="truncate">{category.name}</span>
    </span>
  )
}

interface TimeCategorySelectProps {
  value: string | null | undefined
  /**
   * Called with the picked category (null for "No category"). The category
   * carries `billableDefault` so callers can apply it to the billable switch.
   */
  onChange: (categoryId: string | null, category: TimeCategory | null) => void
  /**
   * The entry's current category, shown even when it has since been
   * archived and no longer appears in the active list.
   */
  currentCategory?: TimeEntryCategorySummary | null
  size?: 'default' | 'compact'
  /** Visible label above the control; omitted for compact grid cells. */
  label?: string
  disabled?: boolean
  id?: string
  className?: string
  triggerClassName?: string
  placeholder?: string
  'aria-label'?: string
}

/**
 * Picks the kind of work (Client work, Admin, Training…) for a time entry.
 * Renders nothing when the organization has no categories, so personal
 * accounts and orgs that never set them up see no extra control.
 */
export function TimeCategorySelect({
  value,
  onChange,
  currentCategory,
  size = 'default',
  label,
  disabled,
  id,
  className,
  triggerClassName,
  placeholder = 'No category',
  'aria-label': ariaLabel = 'Category',
}: TimeCategorySelectProps) {
  const generatedId = useId()
  const controlId = id ?? generatedId
  const { data: categories = [] } = useTimeCategoriesQuery()
  const activeCategories = categories.filter(category => category.isActive)

  // Keep an archived (or not yet loaded) current category selectable.
  const options: Array<
    Pick<TimeCategory, 'id' | 'name' | 'color'> & { isArchived?: boolean }
  > = [...activeCategories]
  if (value && !options.some(option => option.id === value)) {
    const known = categories.find(category => category.id === value)
    const fallback =
      known ?? (currentCategory?.id === value ? currentCategory : null)
    if (fallback) {
      options.push({
        id: fallback.id,
        name: fallback.name,
        color: fallback.color ?? null,
        isArchived: true,
      })
    }
  }

  if (options.length === 0) return null

  const isCompact = size === 'compact'
  const selectedOption = value
    ? (options.find(option => option.id === value) ?? null)
    : null

  return (
    <div className={cn(label && 'space-y-1.5', className)}>
      {label && (
        <Label htmlFor={controlId} className="text-xs">
          {label}
        </Label>
      )}
      <Select
        value={value || NO_CATEGORY}
        onValueChange={next => {
          if (next === NO_CATEGORY) {
            onChange(null, null)
            return
          }
          onChange(
            next,
            categories.find(category => category.id === next) ?? null,
          )
        }}
        disabled={disabled}
      >
        <SelectTrigger
          id={controlId}
          aria-label={label ? undefined : ariaLabel}
          className={cn(
            isCompact && 'h-7 gap-1 px-2 text-xs',
            !value && 'text-muted-foreground',
            triggerClassName,
          )}
        >
          <SelectValue placeholder={placeholder}>
            {selectedOption ? (
              <span className="inline-flex min-w-0 items-center gap-1.5">
                <TimeCategoryDot color={selectedOption.color} />
                <span className="truncate">{selectedOption.name}</span>
              </span>
            ) : (
              <span className="text-muted-foreground">{placeholder}</span>
            )}
          </SelectValue>
        </SelectTrigger>
        <SelectContent>
          <SelectItem value={NO_CATEGORY}>
            <span className="text-muted-foreground">No category</span>
          </SelectItem>
          {options.map(option => (
            <SelectItem key={option.id} value={option.id}>
              <span className="inline-flex min-w-0 items-center gap-2">
                <TimeCategoryDot color={option.color} />
                <span className="truncate">{option.name}</span>
                {option.isArchived && (
                  <span className="text-[11px] text-muted-foreground">
                    Archived
                  </span>
                )}
              </span>
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  )
}
